import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import projects from "../data/projects";
import { arendalDaily, arendalFacts } from "../data/arendalsuka";
import "../styles/projects.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const count = value => value.toLocaleString("en-GB");
const shortDate = iso => {
  const [, month, day] = iso.split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1]}`;
};

/* Arendalsuka: daily peak presence from the published analysis. Part days at
   either end are left out, so every point is a full day's peak. */
const arendalCurve = (() => {
  const days = arendalDaily.filter(day => !day.partial);
  const yMax = 33000;
  const x = index => (index / (days.length - 1)) * 100;
  const y = value => 40 - (value / yMax) * 40;
  const line = days.map((day, index) => `${index ? "L" : "M"}${x(index).toFixed(2)} ${y(day.peak).toFixed(2)}`).join(" ");
  const fest = days
    .map((day, index) => (day.d >= arendalFacts.fest_from && day.d <= arendalFacts.fest_to ? index : -1))
    .filter(index => index >= 0);
  const half = 50 / (days.length - 1);
  const peakIndex = days.findIndex(day => day.peak === arendalFacts.peak);
  const ordinary = days.filter(day => day.d < arendalFacts.fest_from || day.d > arendalFacts.fest_to).map(day => day.peak);
  return {
    line,
    area: `${line} L100 40 L0 40 Z`,
    band: { x: x(fest[0]) - half, width: x(fest[fest.length - 1]) - x(fest[0]) + half * 2 },
    baselineY: y(arendalFacts.baseline),
    peak: { left: x(peakIndex), top: (y(arendalFacts.peak) / 40) * 100 },
    first: days[0].d,
    last: days[days.length - 1].d,
    low: Math.min(...ordinary),
    high: Math.max(...ordinary),
  };
})();

const ArendalThumb = () => {
  const { band, baselineY, peak, first, last, low, high } = arendalCurve;
  const year = arendalFacts.fest_from.slice(0, 4);
  const peakDay = arendalFacts.peak_when.split(", ")[0];
  return (
    <div
      className="project-visual project-visual--chart"
      role="img"
      aria-label={`Chart of daily peak presence in Arendal sentrum, ${shortDate(first)} to ${shortDate(last)} ${year}. Days outside the festival range from ${count(low)} to ${count(high)} estimated devices; the peak is ${count(arendalFacts.peak)} on ${peakDay}, during Arendalsuka. The July median is ${count(arendalFacts.baseline)}.`}
    >
      <span className="project-visual-tag">Estimated devices</span>
      <div className="thumb-chart-plot">
        <svg viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
          <rect x={band.x} y="0" width={band.width} height="40" className="thumb-chart-band" />
          <line x1="0" y1={baselineY} x2="100" y2={baselineY} className="thumb-chart-baseline" vectorEffect="non-scaling-stroke" />
          <path d={arendalCurve.area} className="thumb-chart-area" />
          <path d={arendalCurve.line} className="thumb-chart-line" vectorEffect="non-scaling-stroke" />
        </svg>
        <i className="thumb-chart-peak" style={{ left: `${peak.left}%`, top: `${peak.top}%` }} />
        <span className="thumb-chart-peak-label" style={{ right: `calc(${100 - peak.left}% + 9px)`, top: `${peak.top}%` }}>{count(arendalFacts.peak)} peak</span>
        <span className="thumb-chart-median" style={{ top: `${(baselineY / 40) * 100}%` }}>July median {count(arendalFacts.baseline)}</span>
        <span className="thumb-chart-band-label" style={{ left: `${band.x + band.width / 2}%` }}>Arendalsuka</span>
      </div>
      <span className="thumb-chart-axis"><b>{shortDate(first)}</b><b>Daily peak</b><b>{shortDate(last)} {year}</b></span>
    </div>
  );
};

const CREDIT_LINK = { target: "_blank", rel: "noreferrer" };

const May17Thumb = () => (
  <div className="project-visual project-visual--photo">
    <img src="/may17-oslo-parade.jpg" alt="Constitution Day parade on Karl Johans gate in Oslo on 17 May 2010, lined with Norwegian flags" loading="lazy" />
    <small className="project-visual-credit project-visual-credit--top">
      Photo (cropped): <a href="https://commons.wikimedia.org/wiki/File:Constitution_Day_parade_passing_by_the_Domus_Bibliotheca_and_Domus_Academica,_University_of_Oslo_-_20100517-01.jpg" {...CREDIT_LINK}>Bjoertvedt / Wikimedia Commons</a>, <a href="https://creativecommons.org/licenses/by-sa/3.0/no/deed.en" {...CREDIT_LINK}>CC BY-SA 3.0 NO</a>
    </small>
  </div>
);

const VaulenThumb = () => (
  <div
    className="project-visual project-visual--map"
    role="img"
    aria-label="Street map of Vaulen and Sørmarka in Stavanger marking Vaulen School, the interim school at Sørmarka and the crossing on Sørmarkveien used by 386 modelled pupil routes"
  >
    <svg viewBox="110 70 680 450" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <image href="/vaulen-basemap.jpg" x="0" y="0" width="891" height="686" />
      <line x1="432" y1="416" x2="310" y2="226" className="thumb-vaulen-move" />
      <circle cx="360" cy="175" r="22" className="thumb-vaulen-crossing" />
      <circle cx="432" cy="416" r="12" className="thumb-vaulen-school" />
      <circle cx="310" cy="226" r="12" className="thumb-vaulen-school thumb-vaulen-school--interim" />
      <text x="412" y="425" textAnchor="end" className="thumb-map-label">Vaulen School</text>
      <text x="290" y="236" textAnchor="end" className="thumb-map-label">Sørmarka</text>
      <text x="392" y="184" className="thumb-map-label thumb-map-label--strong">386 routes</text>
    </svg>
    <span className="project-visual-tag">Modelled</span>
    <small className="project-visual-credit">
      © <a href="https://www.openstreetmap.org/copyright" {...CREDIT_LINK}>OpenStreetMap</a> contributors
    </small>
  </div>
);

// Cropped to the best candidate site and the northern edge of the Drøbak grid.
const FrognThumb = () => (
  <div
    className="project-visual project-visual--map"
    role="img"
    aria-label="Map of candidate grocery-store sites in Frogn, shaded by modelled spending kept in Frogn. The best site is labelled: 29 MNOK a year kept in Frogn."
  >
    <svg viewBox="210 110 600 390" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <image href="/frogn/candidate-sites.jpg" x="0" y="0" width="1000" height="800" />
    </svg>
    <span className="project-visual-tag">Modelled</span>
    <small className="project-visual-credit">
      © CARTO © <a href="https://www.openstreetmap.org/copyright" {...CREDIT_LINK}>OpenStreetMap</a> contributors
    </small>
  </div>
);

const FornebuThumb = () => (
  <div className="project-visual project-visual--map">
    <img
      src="/fornebu/forecast-still.jpg"
      alt="Still from our forecast video at Saturday 18:00 of the closure: the forecast tailback on the Oslo-bound E18 reaches back towards Sandvika, with 4,675 vehicles in the queue and 165 minutes of added delay at the peak"
      loading="lazy"
    />
    <span className="project-visual-tag">Forecast</span>
    <small className="project-visual-credit">
      Map data © <a href="https://www.openstreetmap.org/copyright" {...CREDIT_LINK}>OpenStreetMap</a> contributors
    </small>
  </div>
);

const thumbs = {
  arendalsuka: ArendalThumb,
  "may-17th": May17Thumb,
  "vaulen-school": VaulenThumb,
  "frogn-city-development": FrognThumb,
  "road-closure-fornebu": FornebuThumb,
};

const ProjectVisual = ({ slug }) => {
  const Thumb = thumbs[slug];
  return Thumb ? <Thumb /> : <div className="project-visual project-visual--empty" aria-hidden="true" />;
};

const ProjectsPage = () => (
  <div className="min-h-screen bg-paper">
    <Navbar />
    <main className="pt-16">
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-widest text-[#2e7d32] uppercase mb-4">Projects</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ink max-w-4xl leading-[1.08]">
            Case studies from Arendal, Oslo, Stavanger, Frogn and Fornebu
          </h1>
          <p className="mt-6 text-lg md:text-xl text-ink-muted max-w-2xl leading-relaxed">
            A festival week, Constitution Day, a planned school move, a municipal planning study and an E18 lane closure. We measure presence from aggregated mobile-network data and model what it cannot cover.
          </p>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map(({ slug, title, summary, place, result, available }) => (
              <article key={slug} className={`project-card${available ? "" : " project-card-disabled"}`} aria-disabled={available ? undefined : "true"}>
                <ProjectVisual slug={slug} />
                <div className="project-card-body">
                  <p className="project-card-kicker">{summary} · {place}</p>
                  <h2 className="font-display text-xl font-semibold text-ink">
                    {available ? <Link to={`/projects/${slug}`} className="project-card-link">{title}</Link> : title}
                  </h2>
                  <p className="project-card-result">{result}</p>
                  {available && <span className="project-card-more" aria-hidden="true">Read case study <ArrowUpRight size={16} /></span>}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  </div>
);

export default ProjectsPage;
